import { Observation } from '@medplum/fhirtypes';

/**
 * The exact international pound and inch. bariendo-be's `eligibility-calculator.ts` uses the same
 * two for signup's eligibility decision and the weight, height and BMI Observations it writes, so
 * signup and the PN card give the same BMI for the same weight and height (BAR-3018). There is no
 * shared package; a parity test on each side pins them.
 */
export const KG_PER_LB = 0.45359237;
export const CM_PER_INCH = 2.54;

/**
 * Converts a body weight Observation quantity to kilograms.
 * Returns undefined if the unit is not recognized.
 */
export function convertWeightToKg(value: number, unitCode: string | undefined): number | undefined {
  if (unitCode === undefined || unitCode === '') {
    return undefined;
  }
  const code = unitCode.toLowerCase();
  if (code === 'kg' || code === 'kilogram' || code === 'kilograms') {
    return value;
  }
  if (
    code === '[lb_av]' ||
    code === 'lb' ||
    code === 'lbs' ||
    code === '[lb]' ||
    code === 'pound' ||
    code === 'pounds'
  ) {
    return value * KG_PER_LB;
  }
  return undefined;
}

/**
 * Converts a body height Observation quantity to centimeters.
 * Returns undefined if the unit is not recognized.
 */
export function convertHeightToCm(value: number, unitCode: string | undefined): number | undefined {
  if (unitCode === undefined || unitCode === '') {
    return undefined;
  }
  const code = unitCode.toLowerCase();
  if (code === 'cm' || code === 'centimeter' || code === 'centimeters') {
    return value;
  }
  if (code === 'in' || code === '[in_i]' || code === '[in_us]' || code === 'inch' || code === 'inches') {
    return value * CM_PER_INCH;
  }
  if (code === 'ft' || code === 'feet' || code === 'foot' || code === '[ft_i]') {
    return value * 30.48;
  }
  if (code === 'm' || code === 'meter' || code === 'metre' || code === 'meters') {
    return value * 100;
  }
  return undefined;
}

function observationQuantityUnit(observation: Observation | undefined): string | undefined {
  const vq = observation?.valueQuantity;
  return vq?.code ?? vq?.unit;
}

/**
 * Body weight from a FHIR Observation in kilograms.
 * Uses {@link convertWeightToKg} when a unit is present; if unit is omitted, assumes kilograms
 * (matches legacy chart data that stored kg without UCUM).
 */
export function observationWeightToKg(observation: Observation | undefined): number | undefined {
  const vq = observation?.valueQuantity;
  if (vq?.value === undefined) {
    return undefined;
  }
  const unit = observationQuantityUnit(observation);
  if (unit === undefined || unit === '') {
    return vq.value;
  }
  return convertWeightToKg(vq.value, unit);
}

/**
 * Body height from a FHIR Observation in centimetres — the mirror of
 * {@link observationWeightToKg}. A missing unit is read as centimetres, matching
 * the weight helper's treatment of legacy chart data stored without UCUM.
 */
export function observationHeightToCm(observation: Observation | undefined): number | undefined {
  const vq = observation?.valueQuantity;
  if (vq?.value === undefined) {
    return undefined;
  }
  const unit = observationQuantityUnit(observation);
  if (unit === undefined || unit === '') {
    return vq.value;
  }
  return convertHeightToCm(vq.value, unit);
}

/**
 * BMI = weight (kg) / height (m)² from weight and height observations.
 *
 * Reads both through the lenient observation helpers, so every BMI in the app —
 * the BMI panel, the patient list, the consultation card — agrees on the same
 * two observations, unit-less legacy values included.
 */
export function calculateBMIFromObservations(
  weightObservation: Observation | undefined,
  heightObservation: Observation | undefined
): number | undefined {
  const weightKg = observationWeightToKg(weightObservation);
  const heightCm = observationHeightToCm(heightObservation);
  if (weightKg === undefined || heightCm === undefined || heightCm <= 0) {
    return undefined;
  }

  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

/**
 * Total weight loss as a percentage of starting weight, rounded to one decimal place.
 * Both arguments must use the same mass unit (for example, pounds).
 * Callers must ensure {@link startingWeight} is greater than zero.
 */
export function totalWeightLossPercent(weightLoss: number, startingWeight: number): number {
  return Math.round((weightLoss / startingWeight) * 1000) / 10;
}
